(() => {
  const ROOT_SELECTOR = '[data-styleomg-collection]';
  const ALL_VALUE = '__all__';
  const FILTER_DELAY = 220;

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const compact = (value) => normalize(value).replace(/[^a-z0-9]+/g, '');
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const typeMatches = (actualValue, requestedValue) => {
    const actual = compact(actualValue);
    const requested = compact(requestedValue);
    if (!actual || !requested) return false;
    if (actual === requested) return true;

    const removeTshirtSuffix = (value) => value.replace(/tshirt$/, '');
    const actualWithoutSuffix = removeTshirtSuffix(actual);
    const requestedWithoutSuffix = removeTshirtSuffix(requested);

    return (
      actualWithoutSuffix.length > 5 &&
      requestedWithoutSuffix.length > 5 &&
      actualWithoutSuffix === requestedWithoutSuffix
    );
  };

  class StyleOmgCollection {
    constructor(root) {
      this.root = root;
      this.defaultApparelType = root.dataset.defaultApparelType || 'Unisex Standard T-Shirt';
      this.capType = root.dataset.capType || 'Baseball Caps';
      this.baseCollectionUrl = root.dataset.baseCollectionUrl || window.location.pathname;
      this.capFilterUrl = root.dataset.capFilterUrl || '';
      this.capFilterTag = root.dataset.capFilterTag || 'cap';
      this.isCapTagPage = root.dataset.isCapTagPage === 'true';
      this.cards = [...root.querySelectorAll('[data-styleomg-card]')].map(
        (element) => new StyleOmgCard(element)
      );
      this.tabs = [...root.querySelectorAll('[data-styleomg-type-tab]')];
      this.emptyState = root.querySelector('[data-styleomg-empty]');
      this.emptyTitle = root.querySelector('[data-styleomg-empty-title]');
      this.emptyText = root.querySelector('[data-styleomg-empty-text]');
      this.resetButton = root.querySelector('[data-styleomg-reset-type]');
      this.counts = [...root.querySelectorAll('[data-styleomg-visible-count]')];
      this.typeList = root.querySelector('[data-styleomg-type-list]');
      this.typePrev = root.querySelector('[data-styleomg-type-prev]');
      this.typeNext = root.querySelector('[data-styleomg-type-next]');
      this.typeHint = root.querySelector('[data-styleomg-type-hint]');
      this.typeDots = root.querySelector('[data-styleomg-type-dots]');
      this.typeCountLabel = root.querySelector('[data-styleomg-type-count]');
      this.selectedTypeLabel = root.querySelector('[data-styleomg-selected-type]');
      this.filterForm = root.querySelector('[data-styleomg-filter-form]');
      this.filterTypeInput = root.querySelector('[data-styleomg-filter-type]');
      this.sortSelect = root.querySelector('[data-styleomg-sort]');
      this.filterLinks = [...root.querySelectorAll('[data-styleomg-filter-link]')];
      this.loading = root.querySelector('[data-styleomg-loading]');
      this.grid = root.querySelector('[data-styleomg-grid]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.currentTypeValue = this.defaultApparelType;
      this.selectionToken = 0;
      this.typeScrollInteracted = false;

      this.sortFeatured();
      this.renderTypeDots();
      this.bind();
      this.updateHeaderOffset();

      const requested = new URL(window.location.href).searchParams.get('style_type');
      const capTab = this.tabs.find((tab) => tab.dataset.typeMode === 'caps');
      const initialTab =
        (this.isCapTagPage ? capTab : null) ||
        this.tabs.find((tab) => typeMatches(tab.dataset.typeValue, requested)) ||
        this.tabs.find((tab) => tab.dataset.typeValue === requested) ||
        this.tabs.find((tab) => typeMatches(tab.dataset.typeValue, this.defaultApparelType)) ||
        this.tabs.find((tab) => tab.dataset.typeMode === 'type') ||
        this.tabs[0];

      const initialSelection = initialTab
        ? this.selectTab(initialTab, false, true)
        : Promise.resolve();

      Promise.resolve(initialSelection)
        .catch(() => {})
        .finally(() => this.finishInitialRender());

      this.updateTypeScrollControls();
    }

    sortFeatured() {
      const grid = this.root.querySelector('[data-styleomg-grid]');
      if (!grid) return;

      [...grid.children]
        .filter((item) => item.matches('[data-styleomg-card]'))
        .sort((a, b) => {
          const aFeatured = Number(a.dataset.featuredOrder || 9999);
          const bFeatured = Number(b.dataset.featuredOrder || 9999);
          if (aFeatured !== bFeatured) return aFeatured - bFeatured;
          return Number(a.dataset.originalIndex || 0) - Number(b.dataset.originalIndex || 0);
        })
        .forEach((item) => grid.appendChild(item));
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.handleTypeTabClick(tab));
      });

      this.resetButton?.addEventListener('click', () => {
        const tshirtTab =
          this.tabs.find((tab) => typeMatches(tab.dataset.typeValue, this.defaultApparelType)) ||
          this.tabs.find((tab) => tab.dataset.typeMode === 'type') ||
          this.tabs[0];
        if (tshirtTab) this.handleTypeTabClick(tshirtTab);
      });

      this.typePrev?.addEventListener('click', () => this.scrollTypeList(-1));
      this.typeNext?.addEventListener('click', () => this.scrollTypeList(1));
      this.typeHint?.addEventListener('click', () => this.scrollTypeList(1));

      this.filterForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.navigateWithCurrentFilters();
      });

      this.sortSelect?.addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.navigateWithCurrentFilters();
        },
        true
      );

      this.filterLinks.forEach((link) => {
        link.addEventListener('click', () => this.showLoading('page'));
      });

      this.typeList?.addEventListener(
        'scroll',
        () => {
          if (this.typeList.scrollLeft > 8) this.typeScrollInteracted = true;
          this.updateTypeScrollControls();
        },
        { passive: true }
      );

      this.root.addEventListener('click', (event) => {
        const paginationLink = event.target.closest('.pagination a[href]');
        if (!paginationLink) return;
        this.showLoading('page');
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openCard = this.cards.find((card) => card.isOptionsOpen());
        if (openCard) openCard.closeOptions(true);
      });

      document.addEventListener('pointerdown', (event) => {
        const openCard = this.cards.find((card) => card.isOptionsOpen());
        if (!openCard) return;
        if (openCard.element.contains(event.target)) return;
        openCard.closeOptions(false);
      });

      window.addEventListener('resize', () => {
        this.updateHeaderOffset();
        this.updateTypeScrollControls();
        const openCard = this.cards.find((card) => card.isOptionsOpen());
        if (openCard) openCard.positionOptionsPopover();
      });

      window.addEventListener('load', () => this.updateHeaderOffset(), { once: true });
      window.addEventListener('pageshow', () => {
        this.updateHeaderOffset();
        this.hideLoading();
      });
    }

    updateHeaderOffset() {
      const header =
        document.querySelector('#shopify-section-header') ||
        document.querySelector('.section-header') ||
        document.querySelector('.header-wrapper') ||
        document.querySelector('sticky-header');

      let height = 0;
      if (header) {
        const rect = header.getBoundingClientRect();
        height = Math.max(0, Math.round(rect.height || header.offsetHeight || 0));
      }

      this.root.style.setProperty('--styleomg-header-offset', `${height}px`);
    }

    handleTypeTabClick(tab) {
      const mode = tab.dataset.typeMode || 'type';

      if (mode === 'caps' && this.capFilterUrl && !this.isCapTagPage) {
        const destination = new URL(this.capFilterUrl, window.location.origin);
        destination.searchParams.set('style_type', this.capType);
        this.navigateToCollectionView(destination);
        return;
      }

      if (mode !== 'caps' && this.isCapTagPage) {
        const destination = new URL(this.baseCollectionUrl, window.location.origin);

        destination.searchParams.set(
          'style_type',
          mode === 'all' ? ALL_VALUE : tab.dataset.typeValue || this.defaultApparelType
        );

        this.navigateToCollectionView(destination);
        return;
      }

      this.selectTab(tab, true);
    }

    navigateToCollectionView(destinationValue) {
      const destination = new URL(destinationValue, window.location.origin);
      const currentUrl = new URL(window.location.href);
      const sortBy = currentUrl.searchParams.get('sort_by');

      if (sortBy && !destination.searchParams.has('sort_by')) {
        destination.searchParams.set('sort_by', sortBy);
      }

      this.showLoading('page');
      window.location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
    }

    navigateWithCurrentFilters() {
      if (!this.filterForm) return;

      this.syncFilterType();

      const destination = new URL(
        this.filterForm.getAttribute('action') || window.location.pathname,
        window.location.origin
      );
      const formData = new FormData(this.filterForm);

      for (const [name, value] of formData.entries()) {
        const cleanValue = String(value).trim();
        if (!cleanValue) continue;
        destination.searchParams.append(name, cleanValue);
      }

      if (this.currentTypeValue) {
        destination.searchParams.set('style_type', this.currentTypeValue);
      }

      destination.searchParams.delete('page');

      this.showLoading('page');
      window.location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
    }

    async selectTab(tab, updateUrl, immediate = false) {
      const token = ++this.selectionToken;
      const typeValue = tab.dataset.typeValue;
      const typeLabel = tab.dataset.typeLabel;
      const mode = tab.dataset.typeMode || 'type';

      if (!immediate) {
        this.showLoading('local');
        await wait(FILTER_DELAY);
      }

      if (token !== this.selectionToken) return;

      this.currentTypeValue = mode === 'all' ? ALL_VALUE : typeValue;
      if (this.selectedTypeLabel) {
        this.selectedTypeLabel.textContent = typeLabel || 'T-Shirt';
      }
      this.tabs.forEach((item) => item.setAttribute('aria-pressed', item === tab ? 'true' : 'false'));
      this.updateTypeIndicators(tab);
      this.cards.forEach((card) => card.closeOptions(false));

      let visible = 0;
      this.cards.forEach((card) => {
        const shown = card.applyCollectionView({
          mode,
          typeValue,
          defaultApparelType: this.defaultApparelType,
          capType: this.capType,
        });
        if (shown) visible += 1;
      });

      if (mode === 'all') {
        this.sortVisibleCards(this.sortSelect?.value || '');
      } else {
        this.restoreVisibleCardOrder();
      }

      this.counts.forEach((count) => {
        count.textContent = String(visible);
      });

      if (this.emptyState) {
        this.emptyState.hidden = visible > 0;
        if (visible === 0) {
          const isCaps = mode === 'caps' || normalize(typeLabel).includes('cap');
          this.emptyTitle.textContent = isCaps
            ? 'Baseball Caps are currently sold out.'
            : `${typeLabel} is currently unavailable.`;
          this.emptyText.textContent = isCaps
            ? 'We’re sorry for the inconvenience. Please check back soon or explore our personalized apparel.'
            : 'We’re sorry, there are no matching products in this collection right now.';
        }
      }

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('style_type', this.currentTypeValue);
        window.history.replaceState({}, '', url);
      }

      this.updatePaginationLinks(this.currentTypeValue);
      this.syncFilterType();
      this.updateFilterLinks(this.currentTypeValue);
      this.scrollActiveTypeIntoView(tab);

      if (!immediate) {
        this.hideLoading();
        this.animateVisibleCards();
      }
    }

    updatePaginationLinks(typeValue) {
      this.root.querySelectorAll('.pagination a[href]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        if (typeValue) url.searchParams.set('style_type', typeValue);
        else url.searchParams.delete('style_type');
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }

    syncFilterType() {
      if (!this.filterTypeInput) return;

      this.filterTypeInput.value =
        this.currentTypeValue || this.defaultApparelType;

      this.filterTypeInput.disabled = false;
    }

    sortVisibleCards(sortValue) {
      if (!this.grid) return;
      if (!['price-ascending', 'price-descending'].includes(sortValue)) return;

      const direction = sortValue === 'price-descending' ? -1 : 1;
      const visibleCards = this.cards
        .filter((card) => !card.element.hidden)
        .sort((a, b) => {
          const priceDifference =
            (Number(a.currentPriceValue) - Number(b.currentPriceValue)) * direction;

          if (priceDifference !== 0) return priceDifference;

          return (
            Number(a.element.dataset.originalIndex || 0) -
            Number(b.element.dataset.originalIndex || 0)
          );
        });

      visibleCards.forEach((card) => {
        this.grid.appendChild(card.element);
      });
    }

    restoreVisibleCardOrder() {
      if (!this.grid) return;

      this.cards
        .filter((card) => !card.element.hidden)
        .sort(
          (a, b) =>
            Number(a.element.dataset.originalIndex || 0) -
            Number(b.element.dataset.originalIndex || 0)
        )
        .forEach((card) => {
          this.grid.appendChild(card.element);
        });
    }

    updateFilterLinks(typeValue) {
      this.filterLinks.forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        if (typeValue) url.searchParams.set('style_type', typeValue);
        else url.searchParams.delete('style_type');
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }

    getIndicatorTabs() {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      return isMobile
        ? this.tabs.filter((tab) => !tab.classList.contains('styleomg-type-tab--desktop-only'))
        : this.tabs;
    }

    renderTypeDots() {
      if (!this.typeDots) return;
      this.typeDots.innerHTML = '';
      this.indicatorTabs = this.getIndicatorTabs();
      const total = this.indicatorTabs.length;
      if (this.typeCountLabel) {
        this.typeCountLabel.textContent = `${total} styles available`;
      }

      this.indicatorTabs.forEach((tab, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'styleomg-type-picker__dot';
        dot.setAttribute('data-styleomg-type-dot', '');
        dot.setAttribute('aria-label', `View ${tab.dataset.typeLabel || `style ${index + 1}`}`);
        dot.addEventListener('click', () => this.selectTab(tab, true));
        this.typeDots.appendChild(dot);
      });
    }

    updateTypeIndicators(activeTab) {
      if (!this.typeDots) return;
      const tabs = this.indicatorTabs || this.getIndicatorTabs();
      const activeIndex = tabs.indexOf(activeTab);
      [...this.typeDots.children].forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });
    }

    finishInitialRender() {
      if (this.root.dataset.styleomgInitializing !== 'true') return;

      window.requestAnimationFrame(() => {
        this.root.dataset.styleomgInitializing = 'false';
        this.root.setAttribute('aria-busy', 'false');
        this.root.classList.add('is-initialized');

        if (!this.loading) return;

        this.loading.classList.add('is-initial-fading');
        this.loading.setAttribute('aria-hidden', 'true');

        window.setTimeout(() => {
          this.loading.hidden = true;
          this.loading.classList.remove('is-initial-fading');
        }, 220);
      });
    }

    showLoading(mode = 'local') {
      this.root.classList.add('is-loading');
      this.loading?.classList.remove('is-initial-fading');
      this.root.dataset.loadingMode = mode;
      this.root.classList.toggle('is-page-loading', mode === 'page');

      if (!this.loading) return;

      if (mode === 'page') {
        this.loading.hidden = false;
        this.loading.setAttribute('aria-hidden', 'false');
      } else {
        this.loading.hidden = true;
        this.loading.setAttribute('aria-hidden', 'true');
      }
    }

    hideLoading() {
      this.root.classList.remove('is-loading', 'is-page-loading');
      delete this.root.dataset.loadingMode;

      if (!this.loading) return;
      this.loading.classList.remove('is-initial-fading');
      this.loading.hidden = true;
      this.loading.setAttribute('aria-hidden', 'true');
    }

    animateVisibleCards() {
      if (this.reduceMotion) return;

      const visibleCards = this.cards
        .map((card) => card.element)
        .filter((element) => !element.hidden);

      visibleCards.forEach((element, index) => {
        element.classList.remove('styleomg-card-entering');
        element.style.setProperty('--styleomg-enter-delay', `${Math.min(index, 7) * 38}ms`);
        void element.offsetWidth;
        element.classList.add('styleomg-card-entering');

        window.setTimeout(() => {
          element.classList.remove('styleomg-card-entering');
        }, 650);
      });
    }

    scrollTypeList(direction = 1) {
      if (!this.typeList) return;
      const amount = Math.max(this.typeList.clientWidth * 0.72, 220);
      this.typeScrollInteracted = true;
      this.typeList.scrollBy({ left: amount * direction, behavior: 'smooth' });
      window.setTimeout(() => this.updateTypeScrollControls(), 220);
    }

    scrollActiveTypeIntoView(tab) {
      if (!this.typeList || window.matchMedia('(min-width: 990px)').matches) return;
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    updateTypeScrollControls() {
      if (!this.typeList) return;
      const canScroll = this.typeList.scrollWidth > this.typeList.clientWidth + 4;
      const atStart = this.typeList.scrollLeft <= 8;
      const atEnd = this.typeList.scrollLeft + this.typeList.clientWidth >= this.typeList.scrollWidth - 8;

      if (this.typePrev) this.typePrev.hidden = !canScroll || atStart;
      if (this.typeNext) this.typeNext.hidden = !canScroll || atEnd;
      if (this.typeHint) {
        this.typeHint.hidden = !canScroll;
      }
    }
  }

  class StyleOmgCard {
    constructor(element) {
      this.element = element;
      const dataElement = element.querySelector('[data-card-variants]');
      this.data = dataElement ? JSON.parse(dataElement.textContent) : { variants: [] };
      this.variants = this.data.variants || [];
      this.typeIndex = Number(element.dataset.typeOptionIndex);
      this.colorIndex = Number(element.dataset.colorOptionIndex);
      this.sizeIndex = Number(element.dataset.sizeOptionIndex);
      this.isCap = element.dataset.isCap === 'true';
      this.sourceScope = element.dataset.cardSource || 'main';
      this.currentPriceValue = Number(element.dataset.currentPrice || 0);
      this.colorMode = element.dataset.colorMode || 'none';
      this.staticColor = element.dataset.defaultColor || 'Black';
      this.colorPropertyInput = element.querySelector('[data-card-color-property]');
      this.image = element.querySelector('[data-card-image]');
      this.links = [...element.querySelectorAll('[data-card-link]')];
      this.price = element.querySelector('[data-card-price]');
      this.comparePrice = element.querySelector('[data-card-compare-price]');
      this.variantInput = element.querySelector('[data-card-variant-id]');
      this.addButton = element.querySelector('[data-card-add]');
      this.addLabel = element.querySelector('[data-card-add-label]');
      this.colorInputs = [...element.querySelectorAll('[data-card-color]')];
      this.sizeInputs = [...element.querySelectorAll('[data-card-size]')];
      this.selectedColorLabel = element.querySelector('[data-selected-color]');
      this.selectedSizeLabel = element.querySelector('[data-selected-size]');
      this.optionsToggle = element.querySelector('[data-card-options-toggle]');
      this.optionsPanel = element.querySelector('[data-card-selectors]');
      this.optionsClose = element.querySelector('[data-card-options-close]');
      this.summaryColor = element.querySelector('[data-card-summary-color]');
      this.summarySize = element.querySelector('[data-card-summary-size]');
      this.summarySwatch = element.querySelector('[data-card-summary-swatch]');
      this.globalType = '';
      this.bind();
    }

    bind() {
      this.optionsToggle?.addEventListener('click', () => this.toggleOptions());
      this.optionsClose?.addEventListener('click', () => this.closeOptions(true));

      this.colorInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.refresh({ resetColor: false, resetSize: true });
        });
      });

      this.sizeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.refresh({ resetColor: false, resetSize: false });
        });
      });
    }

    applyCollectionView({
      mode,
      typeValue,
      defaultApparelType,
      capType,
    }) {
      let visible = false;

      if (mode === 'all') {
        visible = this.isCap || this.hasType(defaultApparelType);
        this.globalType = this.isCap ? capType : defaultApparelType;
      } else if (mode === 'caps') {
        visible = this.isCap || this.hasType(typeValue);
        this.globalType = typeValue;
      } else {
        visible = !this.isCap && this.hasType(typeValue);
        this.globalType = typeValue;
      }

      this.element.hidden = !visible;
      if (!visible) return false;

      this.refresh({ resetColor: true, resetSize: true });
      return true;
    }

    hasType(typeValue) {
      if (this.typeIndex < 0) return false;
      return this.variants.some((variant) => typeMatches(variant.options[this.typeIndex], typeValue));
    }

    getSelectedColor() {
      return this.colorInputs.find((input) => input.checked)?.value || this.staticColor || '';
    }

    getSelectedSize() {
      return this.sizeInputs.find((input) => input.checked)?.value || '';
    }

    variantMatchesType(variant) {
      if (this.typeIndex < 0 || this.isCap) return true;
      return typeMatches(variant.options[this.typeIndex], this.globalType);
    }

    baseMatches(variant, color = this.getSelectedColor()) {
      if (!this.variantMatchesType(variant)) return false;
      if (this.colorIndex >= 0 && color && normalize(variant.options[this.colorIndex]) !== normalize(color)) {
        return false;
      }
      return true;
    }

    chooseInput(inputs, preferredValue, allowedValues) {
      const availableInputs = inputs.filter(
        (input) => !input.disabled && (!allowedValues || allowedValues.has(normalize(input.value)))
      );
      const preferred = availableInputs.find(
        (input) => normalize(input.value) === normalize(preferredValue)
      );
      const chosen = preferred || availableInputs[0] || null;
      inputs.forEach((input) => {
        input.checked = input === chosen;
      });
      return chosen?.value || '';
    }

    refresh({ resetColor = false, resetSize = false } = {}) {
      let selectedColor = this.getSelectedColor();
      let availableColors = new Set();

      if (this.colorIndex >= 0 && this.colorInputs.length) {
        availableColors = new Set(
          this.variants
            .filter((variant) => variant.available && this.variantMatchesType(variant))
            .map((variant) => normalize(variant.options[this.colorIndex]))
        );

        this.colorInputs.forEach((input) => {
          input.disabled = !availableColors.has(normalize(input.value));
        });

        const selectedColorIsValid =
          selectedColor &&
          availableColors.has(normalize(selectedColor)) &&
          this.colorInputs.some((input) => input.checked && !input.disabled);

        if (resetColor || !selectedColorIsValid) {
          selectedColor = this.chooseInput(this.colorInputs, 'Black', availableColors);
        }
      }

      if (!selectedColor) selectedColor = this.staticColor || 'Black';
      if (this.selectedColorLabel) this.selectedColorLabel.textContent = selectedColor;
      if (this.colorPropertyInput) this.colorPropertyInput.value = selectedColor;

      let selectedSize = this.getSelectedSize();
      let availableSizes = new Set();

      if (this.sizeIndex >= 0 && this.sizeInputs.length) {
        availableSizes = new Set(
          this.variants
            .filter((variant) => variant.available && this.baseMatches(variant, selectedColor))
            .map((variant) => normalize(variant.options[this.sizeIndex]))
        );

        this.sizeInputs.forEach((input) => {
          input.disabled = !availableSizes.has(normalize(input.value));
        });

        const selectedSizeIsValid =
          selectedSize &&
          availableSizes.has(normalize(selectedSize)) &&
          this.sizeInputs.some((input) => input.checked && !input.disabled);

        if (resetSize || !selectedSizeIsValid) {
          selectedSize = this.chooseInput(this.sizeInputs, 'XL', availableSizes);
        }
      }

      if (this.selectedSizeLabel) this.selectedSizeLabel.textContent = selectedSize;

      const exactVariant = this.variants.find((variant) => {
        if (!this.baseMatches(variant, selectedColor)) return false;
        if (
          this.sizeIndex >= 0 &&
          selectedSize &&
          normalize(variant.options[this.sizeIndex]) !== normalize(selectedSize)
        ) {
          return false;
        }
        return true;
      });

      const priceVariant =
        exactVariant ||
        this.variants.find((variant) => variant.available && this.baseMatches(variant, selectedColor)) ||
        this.variants.find((variant) => this.baseMatches(variant, selectedColor)) ||
        this.variants.find((variant) => variant.available && this.variantMatchesType(variant)) ||
        this.variants.find((variant) => this.variantMatchesType(variant));

      const imageVariant =
        (exactVariant?.image ? exactVariant : null) ||
        this.variants.find(
          (variant) =>
            variant.image &&
            this.baseMatches(variant, selectedColor) &&
            (this.sizeIndex < 0 ||
              !selectedSize ||
              normalize(variant.options[this.sizeIndex]) === normalize(selectedSize))
        ) ||
        this.variants.find((variant) => variant.image && this.baseMatches(variant, selectedColor)) ||
        this.variants.find((variant) => variant.image && this.variantMatchesType(variant));

      if (priceVariant) this.updateDisplay(priceVariant, imageVariant || priceVariant);

      const readyVariant = exactVariant && exactVariant.available ? exactVariant : null;

      if (this.variantInput) {
        this.variantInput.value = readyVariant ? readyVariant.id : '';
        this.variantInput.disabled = !readyVariant;
      }

      if (this.addButton) {
        this.addButton.disabled = !readyVariant;
        this.addButton.setAttribute('aria-disabled', readyVariant ? 'false' : 'true');
      }

      if (this.addLabel) {
        this.addLabel.textContent = readyVariant ? 'Add to cart' : 'Sold out';
      }

      this.updateOptionSummary(selectedColor, selectedSize);
      this.updateOptionToggleVisibility(availableColors, availableSizes);
    }

    updateOptionSummary(selectedColor, selectedSize) {
      if (this.summaryColor) this.summaryColor.textContent = selectedColor || '';
      if (this.summarySize) this.summarySize.textContent = selectedSize || '';

      const selectedColorInput = this.colorInputs.find((input) => input.checked);
      const swatchColor = selectedColorInput?.dataset.swatchColor || selectedColor || '#111111';
      if (this.summarySwatch) {
        this.summarySwatch.style.setProperty('--summary-swatch', swatchColor);
      }
    }

    updateOptionToggleVisibility(availableColors, availableSizes) {
      if (!this.optionsToggle) return;

      const enabledColorCount = this.colorInputs.filter((input) => !input.disabled).length;
      const enabledSizeCount = this.sizeInputs.filter((input) => !input.disabled).length;
      const canChange = enabledColorCount > 1 || enabledSizeCount > 1;

      this.optionsToggle.hidden = false;
      this.optionsToggle.classList.toggle('is-static', !canChange);
      this.optionsToggle.setAttribute('aria-disabled', canChange ? 'false' : 'true');

      if (!canChange) this.closeOptions(false);
    }

    isOptionsOpen() {
      return Boolean(this.optionsPanel && !this.optionsPanel.hidden);
    }

    toggleOptions() {
      if (!this.optionsPanel || !this.optionsToggle || this.optionsToggle.hidden || this.optionsToggle.classList.contains('is-static')) return;
      if (this.optionsPanel.hidden) this.openOptions();
      else this.closeOptions(true);
    }

    openOptions() {
      if (!this.optionsPanel || !this.optionsToggle) return;

      const root = this.element.closest(ROOT_SELECTOR);
      root?.querySelectorAll('[data-card-selectors]:not([hidden])').forEach((panel) => {
        if (panel === this.optionsPanel) return;
        panel.hidden = true;
        panel.removeAttribute('style');
        const card = panel.closest('[data-styleomg-card]');
        card?.classList.remove('is-options-open');
        card?.querySelector('[data-card-options-toggle]')?.setAttribute('aria-expanded', 'false');
      });

      this.optionsPanel.hidden = false;
      this.optionsToggle.setAttribute('aria-expanded', 'true');
      this.element.classList.add('is-options-open');
      this.positionOptionsPopover();
    }

    positionOptionsPopover() {
      if (!this.optionsPanel || !this.optionsToggle || this.optionsPanel.hidden) return;

      const content = this.element.querySelector('.styleomg-product-card__content');
      if (!content) return;

      const cardRect = this.element.getBoundingClientRect();
      const toggleRect = this.optionsToggle.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const viewportPadding = 12;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      const desiredWidth = isMobile
        ? Math.min(260, window.innerWidth - viewportPadding * 2)
        : Math.min(
            Math.max(cardRect.width, 300),
            350,
            window.innerWidth - viewportPadding * 2
          );

      let viewportLeft = cardRect.left;

      if (!isMobile && toggleRect.right - desiredWidth > viewportPadding) {
        viewportLeft = toggleRect.right - desiredWidth;
      }

      if (viewportLeft + desiredWidth > window.innerWidth - viewportPadding) {
        viewportLeft = window.innerWidth - viewportPadding - desiredWidth;
      }

      if (viewportLeft < viewportPadding) {
        viewportLeft = viewportPadding;
      }

      const leftWithinContent = viewportLeft - contentRect.left;
      const topWithinContent = toggleRect.bottom - contentRect.top + 8;

      this.optionsPanel.style.width = `${desiredWidth}px`;
      this.optionsPanel.style.left = `${leftWithinContent}px`;
      this.optionsPanel.style.top = `${topWithinContent}px`;
    }

    closeOptions(returnFocus = false) {
      if (!this.optionsPanel || !this.optionsToggle) return;

      this.optionsPanel.hidden = true;
      this.optionsPanel.removeAttribute('style');
      this.optionsToggle.setAttribute('aria-expanded', 'false');
      this.element.classList.remove('is-options-open');

      if (returnFocus) this.optionsToggle.focus({ preventScroll: true });
    }

    updateDisplay(variant, imageVariant) {
      this.currentPriceValue = Number(variant.priceValue || 0);
      this.element.dataset.currentPrice = String(this.currentPriceValue);

      if (this.price) this.price.textContent = variant.price;

      if (this.comparePrice) {
        const showCompare = Number(variant.compareAtPriceValue) > Number(variant.priceValue);
        this.comparePrice.hidden = !showCompare;
        this.comparePrice.textContent = showCompare ? variant.compareAtPrice : '';
      }

      if (imageVariant?.image && this.image) {
        const nextSrc = imageVariant.image.src;
        const imageChanged = this.image.src !== nextSrc && !this.image.src.endsWith(nextSrc);

        if (imageChanged && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.element.classList.add('is-media-updating');
        }

        this.image.src = nextSrc;
        this.image.srcset = `${imageVariant.image.src360} 360w, ${imageVariant.image.src533} 533w, ${imageVariant.image.src720} 720w, ${imageVariant.image.src} 900w`;
        this.image.alt = imageVariant.image.alt || '';
        if (imageVariant.image.width) this.image.width = imageVariant.image.width;
        if (imageVariant.image.height) this.image.height = imageVariant.image.height;

        if (imageChanged) {
          const finishMediaUpdate = () => {
            window.requestAnimationFrame(() => {
              this.element.classList.remove('is-media-updating');
              this.element.classList.add('is-media-ready');
              window.setTimeout(() => this.element.classList.remove('is-media-ready'), 380);
            });
          };

          if (this.image.complete) finishMediaUpdate();
          else this.image.addEventListener('load', finishMediaUpdate, { once: true });
        }
      }

      const linkUrl = new URL(variant.url || this.data.productUrl, window.location.origin);
      linkUrl.searchParams.set('variant', variant.id);
      if (imageVariant?.image?.mediaId) {
        linkUrl.searchParams.set('styleomg_media', imageVariant.image.mediaId);
      } else {
        linkUrl.searchParams.delete('styleomg_media');
      }

      const finalLink = `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`;
      this.links.forEach((item) => {
        item.href = finalLink;
      });
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.styleomgReady === 'true') return;
      root.dataset.styleomgReady = 'true';
      new StyleOmgCollection(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => boot());
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
